import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import DynamicTableVirtual from "../components/table-virtual";
import Topbar from "../components/topbar";
import AddProvisionPopup from "../components/popup/add-provision";
import DeletePopup from "../components/popup/delete";
import DeletePopupMobile from "../components/popup/mobile/delete-mobile";
import DateConfirmPopup from "../components/popup/date-confirm";
import DateConfirmPopupMobile from "../components/popup/mobile/date-confirm-mobile";
import DescriptionFilter from "../components/filters/description-filter";
import SimpleDateFilter, { SimpleDateFilterHandle } from "../components/filters/simple-date-filter";
import AmountFilter, { AmountFilterHandle } from "../components/filters/amount-filter";
import StatusFilter, { StatusFilterHandle } from "../components/filters/status-filter";
import GstProvisionDetail from "../components/popup/gst-provision-detail";
import RelatedTransactionsDetail from "../components/popup/related-transactions-detail";
import ProvisionGroupDetail from "../components/popup/provision-group-detail";
import BottomSheetDialog from "../components/popup/mobile/mobile-bottom-sheet";

import type { Column } from '../components/table-virtual';
import AddBtn from "../components/ui/add-btn";
import HeaderSearch from "../components/ui/header-search";
import provisionImage from '../files/images/transaction.png';
import { useMediaQuery, useTheme, Button, Box, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Chip, RadioGroup, FormControlLabel, Radio, Fab, Collapse, IconButton } from "@mui/material";
import { Menu, Users, Plus, SlidersHorizontal, ChevronDown, ChevronUp, X } from "lucide-react";
import Dropdown from "../components/ui/dropdown";
import TreeDropdown from "../components/tree-dropdown";
import SidebarMobileDrawer from "../components/sidebar-mobile-drawer";
import { useSidebarMobile } from "../providers/sidebar-mobile-context";
import useApi from '../hooks/use-api';
import buildApiRequestConfig from '../api/url-builder';
import routes from '../api/routes';
import { useUser } from '../providers/user-provider';
import { usePermissions } from '../providers/permissions-provider';
import { useToast } from '../providers/toaster-provider';
import { DropdownOption } from "../components/ui/dropdown";
import dayjs from "dayjs";
import { Typography, Tooltip } from "@mui/material";
import { useFeatures } from "../providers/features-provider";
import { OCCURRENCES_PER_YEAR } from "../util/recurrence";
import GeneralProvisionModal, { GeneralProvision } from "../components/popup/general-provision";
import { useSettings } from "../providers/settings-provider";

// A provision's name on a mobile card: wraps onto a second line, then ellipsis.
const MOBILE_NAME_SX = { fontSize: 13, fontWeight: 600, lineHeight: 1.25, whiteSpace: 'normal', wordBreak: 'break-word', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' } as const;

export type Provision = {
    id?: number;
    transactionDate?: string;
    startDate?: string;
    endDate?: string;
    endDateType?: 'on' | 'after' | 'never';
    type: 'daily' | 'weekly' | 'fortnightly' | 'monthly' | 'quarterly' | 'half-yearly' | 'yearly' | 'custom' | 'one-time';
    amount: string;
    description: string;
    recurrencePattern?: string;
    occurrenceCount?: number;
    categoryId: number;
    bankAccountId?: number;
    appliedTaxBreakdown?: { id: number; name: string }[];
    autoProvisionMeta?: string;
    group?: { id: number; name: string } | null;
    // Edit-only: when true, this edit discards any soft-deleted or manually-
    // pushed occurrence history and regenerates every unlinked occurrence
    // fresh from the pattern instead of preserving it (the default).
    regenerateAll?: boolean;
}

// GST provisions use normal recurring types (quarterly/monthly/yearly) so they
// behave like any other recurring provision to the backend, but are tagged via
// autoProvisionMeta so they can stay view-only here and be excluded from the
// horizon-extension cron (see gst-provision-service.js / isGstAutoProvision).
const isGstProvision = (row: Record<string, any>): boolean => {
    if (!row?.autoProvisionMeta) return false;
    try {
        return JSON.parse(row.autoProvisionMeta)?.source === 'gst-provision';
    } catch {
        return false;
    }
};

// ProvisionTemplate.appliedTaxBreakdown is a plain TEXT column (no JSON
// getter, unlike e.g. Transactions.sourceData) - the list endpoint returns
// it as a raw JSON string, not a parsed array. Left unparsed, anything that
// assumes it's an array (merge defaults' .some()/.map() calls, most
// visibly) throws "X.some is not a function" at runtime. Normalize once
// here, at the fetch boundary, so every consumer downstream can rely on it
// always being a real array.
const withParsedTaxBreakdown = (row: any): any => {
    if (Array.isArray(row?.appliedTaxBreakdown)) return row;
    if (typeof row?.appliedTaxBreakdown !== 'string' || !row.appliedTaxBreakdown) {
        return { ...row, appliedTaxBreakdown: [] };
    }
    try {
        const parsed = JSON.parse(row.appliedTaxBreakdown);
        return { ...row, appliedTaxBreakdown: Array.isArray(parsed) ? parsed : [] };
    } catch {
        return { ...row, appliedTaxBreakdown: [] };
    }
};

// Tags column: derives a label/color per known auto-generation source, so any
// future auto-generated template type (tagged via autoProvisionMeta.source,
// same pattern as GST) gets its own tag here without touching the Recurrence
// column or the row-restriction logic.
export const AUTO_PROVISION_TAGS: Record<string, { label: string; color: string }> = {
    'gst-provision': { label: 'GST', color: '#607D8B' },
};

// Also used by RelatedTransactionsDetail's recurrence badge, so the color
// stays consistent between the Provisions table's Recurrence column and
// that modal's header.
export const getRecurrenceColor = (recurrence: string) => {
    switch (recurrence) {
        case 'daily':
            return '#FF5722';
        case 'weekly':
            return '#3F51B5';
        case 'fortnightly':
            return '#2196F3';
        case 'monthly':
            return 'var(--color-primary)';
        case 'quarterly':
            return '#9C27B0';
        case 'half-yearly':
            return '#FF9800';
        case 'yearly':
            return '#4CAF50';
        case 'custom':
            return 'var(--color-primary)';
        default:
            return '#c7c7c7';
    }
};

export const getProvisionTags = (row: Record<string, any>): { label: string; color: string }[] => {
    if (!row?.autoProvisionMeta) return [];
    try {
        const source = JSON.parse(row.autoProvisionMeta)?.source;
        const tag = source ? AUTO_PROVISION_TAGS[source] : undefined;
        return tag ? [tag] : [];
    } catch {
        return [];
    }
};

// The Income / Expense totals' periods - provisions that recur at different
// rates (weekly, monthly, yearly...) are converted to the same period.
const TOTALS_PERIODS = {
    week: { label: 'per week', perYear: 52 },
    month: { label: 'per month', perYear: 12 },
    quarter: { label: 'per quarter', perYear: 4 },
    year: { label: 'per year', perYear: 1 },
} as const;

const Provisions: React.FC = () => {
    const { showSuccess, showError } = useToast();
    const { user } = useUser();
    const { formatDate, formatAmount } = useSettings();
    const { can } = usePermissions();
    const { isVisibleInNav } = useFeatures();
    // The original "grouping" feature (display-only collapse into a summary
    // row via ProvisionGroup) was the wrong approach - real merging (this
    // page's default now) replaces it. Kept working and reachable only when
    // this flag is explicitly turned on for an entity/country via Admin >
    // Feature Flags, in case it's wanted again later.
    const legacyGroupingEnabled = isVisibleInNav('legacy_provision_grouping');
    const canCreate = can('provisions', 'create');
    const canUpdate = can('provisions', 'update');
    const canDelete = can('provisions', 'delete');
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const DeleteModal = isMobile ? DeletePopupMobile : DeletePopup;
    const DateConfirmModal = isMobile ? DateConfirmPopupMobile : DateConfirmPopup;
    const { sidebarOpen, setSidebarOpen } = useSidebarMobile();
    const [data, setData] = React.useState<Provision[]>([]);
    const [openModal, setOpenModal] = React.useState<string>('');
    const [selectedProvision, setSelectedProvision] = React.useState<Provision | null>(null);
    const [categories, setCategories] = React.useState<DropdownOption[]>([]);
    // Raw hierarchical tree (id/name/children) as returned by the API -
    // kept alongside the flattened `categories` above so the inline
    // category editor can use the same searchable TreeDropdown as the
    // Transactions page instead of a flat list.
    const [hierarchicalCategories, setHierarchicalCategories] = React.useState<any[]>([]);
    const [editingProvisionId, setEditingProvisionId] = React.useState<number | null>(null);
    const [columnFilters, setColumnFilters] = React.useState<Record<string, any>>({});
    // Default sort: recurrence (shortest period first), then start date
    // descending within each recurrence - matches getProvisionTemplatesByEntity's
    // own default order clause (provision-template-controller.js) exactly,
    // set explicitly here (rather than left as '') just so the Recurrence
    // column header shows its sort-direction arrow on initial load instead
    // of looking unsorted.
    const [sortBy, setSortBy] = React.useState<string>('recurrencePattern');
    const [sortOrder, setSortOrder] = React.useState<'asc' | 'desc'>('asc');
    // Clicking a column's header label/sort arrow now sorts immediately,
    // separately from opening that column's filter popover (which still
    // offers the same "Sort By X" option, for parity). Same
    // click-to-toggle convention as table-virtual.tsx's own generic sort
    // button: switching to a new column defaults to ascending, clicking the
    // already-active column toggles direction.
    const handleColumnSort = (sortByValue: string) => {
        setSortOrder(prevOrder => (sortBy === sortByValue && prevOrder === 'asc') ? 'desc' : 'asc');
        setSortBy(sortByValue);
    };
    const [terminateEndDate, setTerminateEndDate] = React.useState<string>('');
    // Mobile-only combined "Sort & Filter" bottom sheet - consolidates the
    // per-column filter popups (otherwise unreachable on mobile, since the
    // card view has no column header row to tap) behind one button next to
    // the search bar, same pattern as the Transactions page's FiltersMobile.
    const [combinedFilterOpen, setCombinedFilterOpen] = React.useState(false);
    const [expandedFilterSections, setExpandedFilterSections] = React.useState<Set<string>>(new Set(['startDate']));
    const startDateFilterRef = React.useRef<SimpleDateFilterHandle>(null);
    const endDateFilterRef = React.useRef<SimpleDateFilterHandle>(null);
    const recurrenceFilterRef = React.useRef<StatusFilterHandle>(null);
    const amountCombinedFilterRef = React.useRef<AmountFilterHandle>(null);
    const categoryCombinedFilterRef = React.useRef<StatusFilterHandle>(null);
    const draftColumnFiltersRef = React.useRef<Record<string, any>>({});

    // Grouping - a checkbox per row lets the user tag several provision
    // templates as one group; the Provisions page then collapses a group's
    // members into a single summary row showing just the group name (see
    // displayData below).
    const [selectedIndices, setSelectedIndices] = React.useState<number[]>([]);
    const [groupDialogOpen, setGroupDialogOpen] = React.useState(false);
    const [newGroupName, setNewGroupName] = React.useState('');
    const [groupMode, setGroupMode] = React.useState<'new' | 'existing'>('new');
    const [targetGroupId, setTargetGroupId] = React.useState<string>('');
    const [viewingGroupId, setViewingGroupId] = React.useState<number | null>(null);
    const [confirmUngroupId, setConfirmUngroupId] = React.useState<number | null>(null);

    // Merge - the default behavior now (legacyGroupingEnabled off): selected
    // templates get combined into one new template via AddProvisionPopup's
    // merge mode, and the sources are retired server-side on submit.
    const [mergeSourceIds, setMergeSourceIds] = React.useState<number[] | null>(null);
    const [mergeSourcesForDisplay, setMergeSourcesForDisplay] = React.useState<Provision[]>([]);
    const [mergeYearlyTotal, setMergeYearlyTotal] = React.useState<number>(0);
    const [mergeDefaults, setMergeDefaults] = React.useState<{ startDate: string; endDate: string; endDateType: 'on' | 'after' | 'never'; categoryId?: number; bankAccountId?: number; appliedTaxBreakdown?: { id: number; name: string }[]; description: string } | null>(null);

    // Lazy loading (infinite scroll) - see requestedProvisionPageRef comment
    // on invoices.tsx's equivalent for why a ref (not just state) tracks the
    // requested page.
    const PROVISIONS_PAGE_SIZE = 50;
    const [provisionsPage, setProvisionsPage] = React.useState(1);
    const [hasMoreProvisions, setHasMoreProvisions] = React.useState(false);
    const [isLoadingMoreProvisions, setIsLoadingMoreProvisions] = React.useState(false);
    const [totalProvisionsCount, setTotalProvisionsCount] = React.useState<number | null>(null);
    // Income / expense across every provision the current filters match
    // (provision-template-controller.js), shown beside the search.
    // As yearly amounts (each provision times how often it recurs a year) -
    // shown per the chosen period, no refetch needed to switch.
    const [provisionTotals, setProvisionTotals] = React.useState<{ yearlyIncome: number; yearlyExpense: number } | null>(null);
    const [totalsPeriod, setTotalsPeriod] = React.useState<keyof typeof TOTALS_PERIODS>('month');
    const requestedProvisionPageRef = React.useRef(1);

    const [fetch, setFetch] = useApi();
    const [create, setCreate] = useApi();
    const [update, setUpdate] = useApi();
    const [remove, setRemove] = useApi();
    const [terminate, setTerminate] = useApi();
    const [fetchCategories, setFetchCategories] = useApi();
    const [group, setGroup] = useApi();
    const [ungroup, setUngroup] = useApi();
    const [removeGroupMembers, setRemoveGroupMembers] = useApi();
    const [mergeApi, setMergeApi] = useApi();
    const [fetchGeneral, setFetchGeneral] = useApi<{ data: GeneralProvision[] }>();
    const [editingGeneral, setEditingGeneral] = React.useState<GeneralProvision | null>(null);
    const [generalStatus, setGeneralStatus] = useApi<{ data: GeneralProvision }>();

    React.useEffect(() => {
        const { data, isLoading, error } = generalStatus;
        if (data && !isLoading) {
            showSuccess(`${data.data.description} ${data.data.enabled ? 'enabled' : 'disabled'}`);
            refetchGeneral();
        }
        if (error) showError(typeof error === 'string' ? error : 'Failed to update status');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [generalStatus]);

    const toggleGeneralStatus = (row: Record<string, any>) => {
        const { url, method } = buildApiRequestConfig(routes.generalProvisions.setStatus, [
            { param: 'entityId', value: user?.entityId },
            { param: 'kind', value: row.kind },
        ]);
        setGeneralStatus({ url, method, body: { enabled: !row.enabled } });
    };

    React.useEffect(() => {
        document.title = "FineAnts | Provisions";
    }, []);

    const refetchGeneral = React.useCallback(() => {
        if (user?.entityId == null) return;
        setFetchGeneral(buildApiRequestConfig(routes.generalProvisions.fetchByEntity, [{ param: 'entityId', value: user.entityId }]));
    }, [user?.entityId, setFetchGeneral]);
    React.useEffect(() => { refetchGeneral(); }, [refetchGeneral]);

    React.useEffect(() => {
        requestedProvisionPageRef.current = 1;
        const params = [
            { param: 'entityId', value: user?.entityId },
            { param: 'sortBy', value: sortBy },
            { param: 'sortOrder', value: sortOrder },
            { param: 'description', value: columnFilters.descriptionKeyword },
            { param: 'startDateFrom', value: columnFilters.startDateFrom },
            { param: 'startDateTo', value: columnFilters.startDateTo },
            { param: 'endDateFrom', value: columnFilters.endDateFrom },
            { param: 'endDateTo', value: columnFilters.endDateTo },
            { param: 'hideExpiredRecurring', value: columnFilters.hideExpiredRecurring !== false },
            { param: 'recurrencePatterns', value: columnFilters.recurrencePatterns?.length ? columnFilters.recurrencePatterns.join(',') : undefined },
            { param: 'categoryIds', value: columnFilters.categoryIds?.length ? columnFilters.categoryIds.join(',') : undefined },
            { param: 'minAmount', value: columnFilters.amountKeyword?.[0] },
            { param: 'maxAmount', value: columnFilters.amountKeyword?.[1] },
            { param: 'page', value: 1 },
            { param: 'pageSize', value: PROVISIONS_PAGE_SIZE },
        ];
        const { url, method } = buildApiRequestConfig(routes.provisionTemplate.fetchByEntity, params);
        setFetch({ url, method });
    }, [user, create, update, remove, terminate, group, ungroup, removeGroupMembers, mergeApi, columnFilters, sortBy, sortOrder]);

    const handleLoadMoreProvisions = React.useCallback(() => {
        if (!user?.entityId || isLoadingMoreProvisions || !hasMoreProvisions) return;
        const nextPage = provisionsPage + 1;
        requestedProvisionPageRef.current = nextPage;
        setIsLoadingMoreProvisions(true);
        const params = [
            { param: 'entityId', value: user.entityId },
            { param: 'sortBy', value: sortBy },
            { param: 'sortOrder', value: sortOrder },
            { param: 'description', value: columnFilters.descriptionKeyword },
            { param: 'startDateFrom', value: columnFilters.startDateFrom },
            { param: 'startDateTo', value: columnFilters.startDateTo },
            { param: 'endDateFrom', value: columnFilters.endDateFrom },
            { param: 'endDateTo', value: columnFilters.endDateTo },
            { param: 'hideExpiredRecurring', value: columnFilters.hideExpiredRecurring !== false },
            { param: 'recurrencePatterns', value: columnFilters.recurrencePatterns?.length ? columnFilters.recurrencePatterns.join(',') : undefined },
            { param: 'categoryIds', value: columnFilters.categoryIds?.length ? columnFilters.categoryIds.join(',') : undefined },
            { param: 'minAmount', value: columnFilters.amountKeyword?.[0] },
            { param: 'maxAmount', value: columnFilters.amountKeyword?.[1] },
            { param: 'page', value: nextPage },
            { param: 'pageSize', value: PROVISIONS_PAGE_SIZE },
        ];
        const { url, method } = buildApiRequestConfig(routes.provisionTemplate.fetchByEntity, params);
        setFetch({ url, method });
    }, [user?.entityId, sortBy, sortOrder, columnFilters, provisionsPage, isLoadingMoreProvisions, hasMoreProvisions, setFetch]);
    React.useEffect(() => {
        const { url, method } = buildApiRequestConfig(routes.categories.fetchByEntity, [{ param: 'entityId', value: user?.entityId }]);
        setFetchCategories({ url, method });
    }, [user]);

    React.useEffect(() => {
        const { data, isLoading, error } = fetch;
        if (data && !isLoading) {
            const page = (data?.provisionTemplates ?? data).map(withParsedTaxBreakdown);
            const requestedPage = requestedProvisionPageRef.current;
            setData(prev => requestedPage === 1 ? page : [...prev, ...page]);
            setProvisionsPage(requestedPage);
            setHasMoreProvisions(!!data?.pagination?.hasMore);
            setTotalProvisionsCount(data?.pagination?.totalItems ?? null);
            if (data?.pagination) setProvisionTotals({ yearlyIncome: data.pagination.yearlyIncome ?? 0, yearlyExpense: data.pagination.yearlyExpense ?? 0 });
            setIsLoadingMoreProvisions(false);
        }
        if (error) {
            showError('Error fetching provision templates');
            setIsLoadingMoreProvisions(false);
        }
    }, [fetch]);

    React.useEffect(() => {
        const { data, isLoading, error } = fetchCategories;
        if (data && !isLoading) {
            // The categories endpoint returns a hierarchical tree (parent
            // categories with nested `children`), not a flat list - a plain
            // .map() over the top level only captures parent categories, so
            // any provision using a sub-category fails every id lookup here
            // and falls back to showing the raw numeric categoryId. Flatten
            // recursively instead, matching transactions-virtual.tsx's
            // flatCategoryDropdownOptions. `value` stays numeric (not
            // stringified) since table-virtual.tsx's dropdown cell compares
            // it against the raw categoryId with strict ===.
            const flatten = (cats: any[]): DropdownOption[] => {
                const result: DropdownOption[] = [];
                cats.forEach((cat: any) => {
                    result.push({ value: cat.id, label: cat.name });
                    if (cat.children && cat.children.length > 0) {
                        result.push(...flatten(cat.children));
                    }
                });
                return result;
            };
            setCategories(flatten(Array.isArray(data) ? data : []));
            setHierarchicalCategories(Array.isArray(data) ? data : []);
        }
        if (error) {
            showError('Error fetching categories');
        }
    }, [fetchCategories]);

    // TreeDropdown's own option shape (id must be a string) - same
    // transform as transactions-virtual.tsx's hierarchicalCategoryOptions,
    // minus the "Select All" entry since this is a single-value editor, not
    // a multi-select filter.
    const hierarchicalCategoryOptions = React.useMemo(() => {
        const transformForTree = (cat: any): any => ({
            id: cat.id.toString(),
            label: cat.name,
            children: cat.children ? cat.children.map(transformForTree) : undefined,
        });
        return (Array.isArray(hierarchicalCategories) ? hierarchicalCategories : []).map(transformForTree);
    }, [hierarchicalCategories]);

    React.useEffect(() => {
        const { data, isLoading, error } = create;
        if (data && !isLoading) {
            showSuccess('Provision created successfully');
            setOpenModal('');
        }
        if (error) {
            showError(error ? error.toString() : 'Error creating provision');
        }
    }, [create]);

    React.useEffect(() => {
        const { data, isLoading, error } = update;
        if (data && !isLoading) {
            showSuccess('Provision updated successfully');
            setOpenModal('');
        }
        if (error) {
            showError(error ? error.toString() : 'Error updating provision');
        }
    }, [update]);

    React.useEffect(() => {
        const { data, isLoading, error } = remove;
        if (data && !isLoading) {
            setOpenModal('');
            setSelectedProvision(null);
            showSuccess('Provision deleted successfully');
        }
        if (error) {
            showError(error ? error.toString() : 'Error deleting provision');
        }
    }, [remove]);

    React.useEffect(() => {
        const { data, isLoading, error } = terminate;
        if (data && !isLoading) {
            setOpenModal('');
            setSelectedProvision(null);
            setTerminateEndDate('');
            showSuccess('Provision terminated successfully');
        }
        if (error) {
            showError(error ? error.toString() : 'Error terminating provision');
        }
    }, [terminate]);

    React.useEffect(() => {
        const { data, isLoading, error } = group;
        if (data && !isLoading) {
            setGroupDialogOpen(false);
            setNewGroupName('');
            setTargetGroupId('');
            setGroupMode('new');
            setSelectedIndices([]);
            showSuccess('Provisions grouped successfully');
        }
        if (error) {
            showError(error ? error.toString() : 'Error grouping provisions');
        }
    }, [group]);

    React.useEffect(() => {
        const { data, isLoading, error } = ungroup;
        if (data && !isLoading) {
            setViewingGroupId(null);
            setConfirmUngroupId(null);
            showSuccess('Group dissolved');
        }
        if (error) {
            showError(error ? error.toString() : 'Error ungrouping provisions');
        }
    }, [ungroup]);

    React.useEffect(() => {
        const { data, isLoading, error } = removeGroupMembers;
        if (data && !isLoading) {
            showSuccess('Removed from group');
        }
        if (error) {
            showError(error ? error.toString() : 'Error removing provisions from group');
        }
    }, [removeGroupMembers]);

    React.useEffect(() => {
        const { data, isLoading, error } = mergeApi;
        if (data && !isLoading) {
            setOpenModal('');
            setMergeSourceIds(null);
            setSelectedIndices([]);
            showSuccess('Provisions merged successfully');
        }
        if (error) {
            showError(error ? error.toString() : 'Error merging provisions');
        }
    }, [mergeApi]);

    // Handle query params for edit mode from transactions page
    React.useEffect(() => {
        const isEdit = searchParams.get('isEdit');
        const templateId = searchParams.get('templateId');
        
        if (isEdit === 'true' && templateId) {
            setEditingProvisionId(Number(templateId));
            setOpenModal('provision');
            // Clean up query params
            setSearchParams({});
        }
    }, [searchParams, setSearchParams]);

    const handleCreateProvision = (newProvision: Provision) => {
        const { url, method } = buildApiRequestConfig(routes.provisionTemplate.create);
        setCreate({ url, method, body: { ...newProvision, entityId: user?.entityId, appliedTaxBreakdown: JSON.stringify(newProvision.appliedTaxBreakdown) } });
    };

    const handleUpdateProvision = (newProvision: Provision) => {
        if(!newProvision.id) return;
        const { url, method } = buildApiRequestConfig(routes.provisionTemplate.update, [{ param: 'id', value: newProvision.id }]);
        setUpdate({ url, method, body: { ...newProvision, entityId: user?.entityId, appliedTaxBreakdown: JSON.stringify(newProvision.appliedTaxBreakdown) } });
    };

    const handleDeleteProvision = (provisionId: number) => {
        const { url, method } = buildApiRequestConfig(routes.provisionTemplate.delete, [{ param: 'id', value: provisionId }]);
        setRemove({ url, method });
    };

    const handleTerminateProvision = (provisionId: number, endDate: string) => {
        const { url, method } = buildApiRequestConfig(routes.provisionTemplate.terminate, [{ param: 'id', value: provisionId }]);
        setTerminate({ url, method, body: { endDate } });
    };

    // Creates a brand new group from the selected (ungrouped) provisions, or
    // adds them to an already-existing group instead - group rows have no
    // checkbox (see canSelect on the actions column), so `ids` here is
    // always a set of plain, currently-ungrouped provision template ids.
    const handleGroupSelected = (ids: number[]) => {
        const { url, method } = buildApiRequestConfig(routes.provisionTemplate.group);
        if (groupMode === 'existing') {
            if (!targetGroupId || ids.length === 0) return;
            setGroup({ url, method, body: { ids, groupId: Number(targetGroupId), entityId: user?.entityId } });
        } else {
            if (!newGroupName.trim() || ids.length < 2) return;
            setGroup({ url, method, body: { ids, groupName: newGroupName.trim(), entityId: user?.entityId } });
        }
    };

    const handleUngroup = (groupId: number) => {
        const { url, method } = buildApiRequestConfig(routes.provisionTemplate.ungroup);
        setUngroup({ url, method, body: { groupId, entityId: user?.entityId } });
    };

    const handleRemoveGroupMembers = (ids: number[]) => {
        if (ids.length === 0) return;
        const { url, method } = buildApiRequestConfig(routes.provisionTemplate.removeGroupMembers);
        setRemoveGroupMembers({ url, method, body: { ids, entityId: user?.entityId } });
    };

    // Words common to every source's description, preserving the first
    // source's word order/casing - e.g. "Netflix Subscription Jan" +
    // "Netflix Subscription Feb" -> "Netflix Subscription".
    const commonDescriptionWords = (descriptions: string[]): string => {
        const wordLists = descriptions.map(d => (d || '').split(/\s+/).filter(Boolean));
        if (wordLists.length === 0 || wordLists.some(w => w.length === 0)) return '';
        const [first, ...rest] = wordLists;
        const restLower = rest.map(w => w.map(x => x.toLowerCase()));
        return first.filter(word => restLower.every(list => list.includes(word.toLowerCase()))).join(' ');
    };

    const handleMergeSelected = (ids: number[]) => {
        if (ids.length < 2) return;
        const sources = data.filter((p): p is Provision & { id: number } => p.id != null && ids.includes(p.id));
        if (sources.some(s => s.type === 'custom')) {
            showError('Custom-recurrence provisions can\'t be merged - deselect them first.');
            return;
        }

        const yearlyTotal = sources.reduce((sum, s) => sum + (Number(s.amount) || 0) * (OCCURRENCES_PER_YEAR[s.type] || 0), 0);

        const startDates = sources.map(s => s.startDate).filter(Boolean) as string[];
        const defaultStartDate = startDates.length
            ? startDates.reduce((earliest, d) => new Date(d) < new Date(earliest) ? d : earliest)
            : '';

        const endDates = sources.filter(s => s.endDateType !== 'never').map(s => s.endDate).filter(Boolean) as string[];
        const defaultEndDate = endDates.length
            ? endDates.reduce((latest, d) => new Date(d) > new Date(latest) ? d : latest)
            : '';
        const defaultEndDateType: 'on' | 'after' | 'never' = defaultEndDate ? 'on' : 'never';

        // Only prefill a field when every source agrees on it - a merge is
        // combining genuinely different templates, so silently picking the
        // "most common" value (the old behavior) could apply a category/
        // bank/tax that some of the sources never actually had. Leaving it
        // blank on disagreement forces a deliberate choice instead.
        const unanimousOrUndefined = <T,>(values: T[]): T | undefined => {
            if (values.length === 0) return undefined;
            const [first, ...rest] = values;
            return rest.every(v => v === first) ? first : undefined;
        };

        const defaultCategoryId = unanimousOrUndefined(sources.map(s => s.categoryId));
        const defaultBankAccountId = unanimousOrUndefined(sources.map(s => s.bankAccountId));

        // Tax breakdown is an array, not a scalar - compare by sorted id set
        // rather than reference equality.
        const taxKey = (tax?: { id: number; name: string }[]) => [...(tax || [])].map(t => t.id).sort((a, b) => a - b).join(',');
        const firstTaxBreakdown = sources[0]?.appliedTaxBreakdown;
        const defaultAppliedTaxBreakdown = sources.every(s => taxKey(s.appliedTaxBreakdown) === taxKey(firstTaxBreakdown))
            ? firstTaxBreakdown
            : undefined;

        const defaultDescription = commonDescriptionWords(sources.map(s => s.description));

        setMergeSourceIds(ids);
        setMergeDefaults({
            startDate: defaultStartDate,
            endDate: defaultEndDate,
            endDateType: defaultEndDateType,
            categoryId: defaultCategoryId,
            bankAccountId: defaultBankAccountId,
            appliedTaxBreakdown: defaultAppliedTaxBreakdown,
            description: defaultDescription,
        });
        setMergeYearlyTotal(yearlyTotal);
        setMergeSourcesForDisplay(sources);
        setOpenModal('merge');
    };

    const handleMergeCreate = (newProvision: Provision, sourceIds: number[]) => {
        const { url, method } = buildApiRequestConfig(routes.provisionTemplate.merge);
        setMergeApi({
            url,
            method,
            body: {
                sourceIds,
                entityId: user?.entityId,
                amount: newProvision.amount,
                description: newProvision.description,
                type: newProvision.type,
                occurrenceCount: newProvision.occurrenceCount,
                recurrencePattern: newProvision.recurrencePattern,
                startDate: newProvision.startDate,
                endDate: newProvision.endDate,
                endDateType: newProvision.endDateType,
                categoryId: newProvision.categoryId,
                bankAccountId: newProvision.bankAccountId,
                appliedTaxBreakdown: JSON.stringify(newProvision.appliedTaxBreakdown),
            },
        });
    };

    const descriptionFilterRef = React.useRef<HTMLDivElement>(null);

    // AmountFilter needs overall min/max bounds for its slider - no
    // server-computed aggregate exists for provisions, so this is a
    // best-effort estimate from whatever's currently loaded (same approach
    // as invoices.tsx's amountBounds). Once a filter narrows `data`, naively
    // recomputing from `data` would shrink these bounds along with it (the
    // slider's max permanently dropping once you filter to a smaller range)
    // - so these only ever widen, never shrink.
    const [amountBounds, setAmountBounds] = React.useState({ min: 0, max: 100000 });
    React.useEffect(() => {
        if (data.length === 0) return;
        const amounts = data.map(d => Number(d.amount) || 0);
        const newMin = Math.min(0, ...amounts);
        const newMax = Math.max(...amounts, 100);
        setAmountBounds(prev => ({
            min: Math.min(prev.min, newMin),
            max: Math.max(prev.max, newMax),
        }));
    }, [data]);

    // Collapses provisions sharing the same group into a single summary row
    // showing the group name, the sum of its members' amounts, the earliest
    // member start date, and the latest member end date (only counting
    // members that actually have one - endDateType 'never' means no end
    // date at all). Other columns are left blank for a group row since they
    // can differ per member. A group's members can't be selected for
    // grouping again - a group can't be grouped, so its checkbox is hidden
    // (see the `actions` column's canSelect below).
    const displayData = React.useMemo(() => {
        // Legacy display-collapse grouping is opt-in via feature flag now -
        // when off, every row shows individually regardless of any leftover
        // `group` tagging in the data.
        if (!legacyGroupingEnabled) return data;
        const groups: Record<number, Provision[]> = {};
        const ungroupedRows: Record<string, any>[] = [];
        data.forEach(p => {
            if (p.group?.id) {
                (groups[p.group.id] ||= []).push(p);
            } else {
                ungroupedRows.push(p);
            }
        });
        const groupRows: Record<string, any>[] = Object.entries(groups).map(([groupId, members]) => {
            const startDates = members.map(m => m.startDate).filter(Boolean) as string[];
            const endDates = members.map(m => m.endDate).filter(Boolean) as string[];
            return {
                id: `group-${groupId}`,
                isGroupRow: true,
                groupId: Number(groupId),
                description: members[0].group?.name || 'Group',
                amount: members.reduce((sum, m) => sum + (Number(m.amount) || 0), 0),
                startDate: startDates.length ? startDates.reduce((earliest, d) => new Date(d) < new Date(earliest) ? d : earliest) : undefined,
                endDate: endDates.length ? endDates.reduce((latest, d) => new Date(d) > new Date(latest) ? d : latest) : undefined,
                memberIds: members.map(m => m.id),
                memberCount: members.length,
            };
        });
        return [...ungroupedRows, ...groupRows];
    }, [data, legacyGroupingEnabled]);

    // Group rows have no checkbox (see canSelect below), so a selected
    // display row is always a plain provision template with a real id.
    const selectedGroupCandidateIds = React.useMemo(() => {
        return selectedIndices
            .map(idx => (displayData[idx] as any)?.id)
            .filter((id): id is number => typeof id === 'number');
    }, [selectedIndices, displayData]);

    // Every distinct group currently on the page, for the "Add to Existing
    // Group" option - derived straight from the flat list rather than
    // displayData since a member's own row carries the group it belongs to.
    const existingGroups = React.useMemo(() => {
        const seen = new Map<number, string>();
        data.forEach(p => {
            if (p.group?.id != null && !seen.has(p.group.id)) seen.set(p.group.id, p.group.name);
        });
        return Array.from(seen.entries()).map(([id, name]) => ({ value: String(id), label: name }));
    }, [data]);

    const viewingGroupMembers = React.useMemo(() => {
        if (viewingGroupId == null) return [];
        return data.filter((p): p is Provision & { id: number } => p.id != null && p.group?.id === viewingGroupId);
    }, [data, viewingGroupId]);
    const viewingGroupName = viewingGroupMembers[0]?.group?.name || 'Group';

    // If removing members dissolved the group entirely (fewer than 2 members
    // left, see removeProvisionsFromGroup's auto-dissolve), the popup being
    // viewed no longer has anything to show - close it.
    React.useEffect(() => {
        if (viewingGroupId !== null && !fetch.isLoading && viewingGroupMembers.length === 0) {
            setViewingGroupId(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewingGroupMembers, fetch.isLoading]);

    // Fixed set of recurrence types this page ever shows (one-time is
    // excluded from this list's whereClause entirely, see backend).
    const recurrenceOptions = [
        { id: 'daily', label: 'Daily' },
        { id: 'weekly', label: 'Weekly' },
        { id: 'fortnightly', label: 'Fortnightly' },
        { id: 'monthly', label: 'Monthly' },
        { id: 'quarterly', label: 'Quarterly' },
        { id: 'half-yearly', label: 'Half-Yearly' },
        { id: 'yearly', label: 'Yearly' },
        { id: 'custom', label: 'Custom' },
    ];

    // Combined mobile filter sheet - each embedded section's onApply writes
    // into this ref instead of straight into columnFilters (5 synchronous
    // calls in one click would each close over the same stale columnFilters
    // and clobber each other); the single Apply button below commits the
    // fully-merged result once at the end. Same pattern as the Transactions
    // page's FiltersMobile combined modal.
    const handleCombinedFilterDraftApply = (params: Record<string, any>) => {
        draftColumnFiltersRef.current = { ...draftColumnFiltersRef.current, ...params };
    };

    const handleApplyAllCombinedFilters = () => {
        draftColumnFiltersRef.current = { ...columnFilters };
        startDateFilterRef.current?.applyNow();
        endDateFilterRef.current?.applyNow();
        recurrenceFilterRef.current?.applyNow();
        amountCombinedFilterRef.current?.applyNow();
        categoryCombinedFilterRef.current?.applyNow();
        setColumnFilters(prev => {
            const { sortBy: draftSortBy, sortDirection: draftSortDirection, ...rest } = draftColumnFiltersRef.current as any;
            if (draftSortBy !== undefined) {
                setSortBy(draftSortBy === null ? 'recurrencePattern' : draftSortBy);
                setSortOrder(draftSortDirection === 'ASC' ? 'asc' : 'desc');
            }
            return { ...prev, ...rest };
        });
        setCombinedFilterOpen(false);
    };

    const handleClearAllCombinedFilters = () => {
        draftColumnFiltersRef.current = {};
        startDateFilterRef.current?.clear();
        endDateFilterRef.current?.clear();
        recurrenceFilterRef.current?.clear();
        amountCombinedFilterRef.current?.clear();
        categoryCombinedFilterRef.current?.clear();
        setColumnFilters({});
    };

    const toggleFilterSection = (section: string) => {
        setExpandedFilterSections(prev => {
            const next = new Set(prev);
            if (next.has(section)) next.delete(section); else next.add(section);
            return next;
        });
    };

    const CombinedFilterSection: React.FC<{ title: string; section: string; children: React.ReactNode }> = ({ title, section, children }) => (
        <Box sx={{ mb: 1, border: '1px solid #e0ecf7', borderRadius: 1.5, overflow: 'hidden' }}>
            <Box
                onClick={() => toggleFilterSection(section)}
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 1,
                    px: 1.5,
                    background: '#f5f9fe',
                    cursor: 'pointer',
                    '&:hover': { background: '#e8f4f0' },
                }}
            >
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'var(--color-default-text)' }}>{title}</Typography>
                {expandedFilterSections.has(section) ? <ChevronUp size={18} color="var(--color-primary)" /> : <ChevronDown size={18} color="var(--color-primary)" />}
            </Box>
            <Collapse in={expandedFilterSections.has(section)}>
                <Box sx={{ p: 1.5, background: 'var(--color-page-background)' }}>
                    {children}
                </Box>
            </Collapse>
        </Box>
    );

    // Map API sortBy values to column IDs
    const getSortColumnId = (sortBy: string | null): string => {
        const sortMapping: Record<string, string> = {
            'recurrencePattern': 'type',
            'category': 'categoryId',
        };
        return sortBy ? (sortMapping[sortBy] || sortBy) : '';
    };

    // Shared with the description column's group-aware customElement below,
    // so a plain (non-group) row's text keeps the same styling it would've
    // gotten from the table's default string renderer.
    const getRowFontSx = (row: Record<string, any>) => ({
        color: !row.endDate || dayjs(row.endDate).isAfter(dayjs().startOf('day')) ? 'var(--color-provision-text)' : 'inherit',
        fontSize: 14,
        fontWeight: 500,
    });

    const columns: Column[] = [
        {
            id: 'startDate',
            label: 'Start Date',
            size: 75,
            type: 'date',
            inlineEdit: false,
            onSort: () => handleColumnSort('startDate'),
            filterable: true,
            filterComponent: (open, anchorEl, onClose, onApply) => (
                <SimpleDateFilter
                    open={open}
                    anchorEl={anchorEl}
                    onClose={onClose}
                    onApply={(params) => {
                        setColumnFilters(prev => ({
                            ...prev,
                            startDateFrom: params.startDate,
                            startDateTo: params.endDate,
                        }));
                        if (params.sortBy) {
                            setSortBy(params.sortBy);
                            setSortOrder(params.sortDirection === 'ASC' ? 'asc' : 'desc');
                        } else if (params.sortBy === null) {
                            setSortBy('');
                            setSortOrder('desc');
                        }
                        onClose();
                    }}
                    currentSort={{ sortBy, sortDirection: sortOrder === 'asc' ? 'ASC' : 'DESC' }}
                    currentStartDate={columnFilters.startDateFrom}
                    currentEndDate={columnFilters.startDateTo}
                    fieldLabel="Start Date"
                    sortByValue="startDate"
                />
            ),
            isFilterActive: () => !!(columnFilters.startDateFrom || columnFilters.startDateTo),
        },
        {
            id: 'endDate',
            label: 'End Date',
            size: 75,
            type: 'date',
            inlineEdit: false,
            // Ongoing provisions: an infinity sign, spelled out on hover.
            emptyDateLabel: <Box component="span" sx={{ fontSize: 18, fontStyle: 'normal', lineHeight: 1 }}>∞</Box>,
            tooltip: (row) => (row.endDate ? null : 'No end date'),
            onSort: () => handleColumnSort('endDate'),
            filterable: true,
            filterComponent: (open, anchorEl, onClose, onApply) => (
                <SimpleDateFilter
                    open={open}
                    anchorEl={anchorEl}
                    onClose={onClose}
                    onApply={(params) => {
                        setColumnFilters(prev => ({
                            ...prev,
                            endDateFrom: params.startDate,
                            endDateTo: params.endDate,
                            ...(params.extraToggleValue !== undefined ? { hideExpiredRecurring: params.extraToggleValue } : {}),
                        }));
                        if (params.sortBy) {
                            setSortBy(params.sortBy);
                            setSortOrder(params.sortDirection === 'ASC' ? 'asc' : 'desc');
                        } else if (params.sortBy === null) {
                            setSortBy('');
                            setSortOrder('desc');
                        }
                        onClose();
                    }}
                    currentSort={{ sortBy, sortDirection: sortOrder === 'asc' ? 'ASC' : 'DESC' }}
                    currentStartDate={columnFilters.endDateFrom}
                    currentEndDate={columnFilters.endDateTo}
                    fieldLabel="End Date"
                    sortByValue="endDate"
                    extraToggle={{ label: 'Hide past end dates', currentValue: columnFilters.hideExpiredRecurring !== false }}
                />
            ),
            isFilterActive: () => !!(columnFilters.endDateFrom || columnFilters.endDateTo) || columnFilters.hideExpiredRecurring === false,
        },
        {
            id: 'type',
            label: 'Recurrence',
            size: 100,
            onSort: () => handleColumnSort('recurrencePattern'),
            mobilePrimary: true,
            mobileOrder: 2,
            mobileHideLabel: true,
            // Fixed width (not content-hugging) so this badge - and Amount
            // after it - line up at the same x-position on every card,
            // instead of drifting left/right per row depending on how long
            // that row's description happens to be.
            mobileWidth: 78,
            // Smaller/tighter than the desktop pill (no letter-spacing, less
            // padding) - the desktop version's sizing assumes a roomy table
            // cell, which a mobile stat block sharing a row with two other
            // fields doesn't have.
            mobileElement: (value: any, row: any) => row.isGroupRow ? null : (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <span
                        style={{
                            background: getRecurrenceColor(value),
                            borderRadius: '10px',
                            padding: '1px 6px',
                            color: '#ffffffff',
                            fontSize: '10px',
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                        }}
                    >{value.toUpperCase()}</span>
                </Box>
            ),
            filterable: true,
            filterComponent: (open, anchorEl, onClose, onApply) => (
                <StatusFilter
                    open={open}
                    anchorEl={anchorEl}
                    onClose={onClose}
                    title="Filter by Recurrence"
                    options={recurrenceOptions}
                    selectedIds={columnFilters.recurrencePatterns?.length ? columnFilters.recurrencePatterns : recurrenceOptions.map(o => o.id)}
                    sortByValue="recurrencePattern"
                    sortFieldLabel="Recurrence"
                    currentSort={{ sortBy, sortDirection: sortOrder === 'asc' ? 'ASC' : 'DESC' }}
                    onApply={(result) => {
                        setColumnFilters(prev => ({
                            ...prev,
                            recurrencePatterns: result.selectedIds.length < recurrenceOptions.length ? result.selectedIds : undefined,
                        }));
                        if (result.sortBy) {
                            setSortBy(result.sortBy);
                            setSortOrder(result.sortDirection === 'ASC' ? 'asc' : 'desc');
                        }
                        onClose();
                    }}
                />
            ),
            isFilterActive: () => !!columnFilters.recurrencePatterns?.length,
            customElement: (value, row) => {
                if (row.isGroupRow) return null;
                return (
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <span
                            style={{
                                background: getRecurrenceColor(value),
                                borderRadius: '12px',
                                padding: '2px 8px',
                                color: '#ffffffff',
                                marginLeft: '8px',
                                fontSize: '12px',
                                letterSpacing: '1px',
                                fontWeight: 600,
                            }}
                        >{value.toUpperCase()}</span>
                    </Box>
                );
            }
        },
        {
            id: 'tags',
            label: 'Tags',
            size: 90,
            customElement: (value, row) => {
                const tags = getProvisionTags(row);
                if (!tags.length) return null;
                return (
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                        {tags.map(tag => (
                            <span
                                key={tag.label}
                                style={{
                                    background: tag.color,
                                    borderRadius: '12px',
                                    padding: '2px 8px',
                                    color: '#ffffffff',
                                    fontSize: '12px',
                                    letterSpacing: '1px',
                                    fontWeight: 600,
                                }}
                            >{tag.label}</span>
                        ))}
                    </Box>
                );
            }
        },
        {
            id: 'amount',
            label: 'Amount',
            size: 100,
            type: 'currency',
            onSort: () => handleColumnSort('amount'),
            mobilePrimary: true,
            mobileOrder: 3,
            // Provisions only ever has one currency field, unlike the
            // Transactions page (Amount + Cash Position) where the label is
            // what tells them apart - here it's redundant.
            mobileHideLabel: true,
            // Fixed width for the same alignment reason as Description/
            // Recurrence - wide enough for a large negative amount (e.g.
            // "$ -123,456.78") without truncating, unlike Description which
            // is already bounded to a fixed character count.
            mobileWidth: 110,
            mobileAlign: 'right',
            filterable: true,
            filterComponent: (open, anchorEl, onClose, onApply) => (
                <AmountFilter
                    open={open}
                    anchorEl={anchorEl}
                    onClose={onClose}
                    min={amountBounds.min}
                    max={amountBounds.max}
                    onApply={(params: any) => {
                        setColumnFilters(prev => ({
                            ...prev,
                            amountKeyword: params.amountKeyword,
                        }));
                        if (params.sortBy) {
                            setSortBy(params.sortBy);
                            setSortOrder(params.sortDirection === 'ASC' ? 'asc' : 'desc');
                        } else if (params.sortBy === null) {
                            setSortBy('');
                            setSortOrder('desc');
                        }
                        onClose();
                    }}
                    currentSort={{ sortBy, sortDirection: sortOrder === 'asc' ? 'ASC' : 'DESC' }}
                    currentAmountKeyword={columnFilters.amountKeyword}
                />
            ),
            isFilterActive: () => !!columnFilters.amountKeyword,
        },
        {
            id: 'description',
            label: 'Description',
            size: 300,
            onSort: () => handleColumnSort('description'),
            mobilePrimary: true,
            mobileOrder: 1,
            // Fixed width, same as Recurrence/Amount below - keeps every
            // card's columns lined up at the same x-position instead of
            // drifting per row based on that row's specific text length.
            // Already truncated to a fixed character count (see
            // mobileElement below), so it doesn't need as much room as
            // Amount, which has to fit large, unbounded currency values.
            mobileWidth: 95,
            // No label on mobile - like the Date field on the Transactions
            // page's card, this is the row's own identity, not one stat
            // among several, so naming it "Description" above the value
            // would be redundant.
            mobileHideLabel: true,
            // Truncated the same way the Transactions page's mobile card
            // truncates its own description line - full text is one tap
            // away (the row opens the edit modal), so the card itself only
            // needs enough to identify the row, not the whole string.
            mobileElement: (value: any, row: any) => (
                // Up to two lines, then an ellipsis - a hard 15-character cut
                // (plus the card's one-line clip) left names like "BRIGHTERBOC".
                <Typography sx={{ ...MOBILE_NAME_SX, color: row.isGroupRow ? 'var(--color-primary)' : getRowFontSx(row).color }}>
                    {value}
                </Typography>
            ),
            customElement: (value, row) => {
                if (!row.isGroupRow) return <Typography sx={{ ...getRowFontSx(row), fontWeight: 400 }}>{value}</Typography>;
                return (
                    <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
                        onClick={() => setViewingGroupId(row.groupId)}
                    >
                        <Users size={14} color="var(--color-primary)" />
                        <Typography sx={{ fontWeight: 600, textDecoration: 'underline', color: 'var(--color-primary)' }}>{value}</Typography>
                        <Chip size="small" label={`${row.memberCount} provisions`} sx={{ height: 20, fontSize: 11 }} />
                    </Box>
                );
            },
            filterable: true,
            filterComponent: (open, anchorEl, onClose, onApply) => (
                <DescriptionFilter
                    open={open}
                    anchorEl={anchorEl || descriptionFilterRef.current}
                    onClose={onClose}
                    onApply={(params) => {
                        setColumnFilters(prev => ({
                            ...prev,
                            descriptionKeyword: params.descriptionKeyword
                        }));
                        if (params.sortBy) {
                            setSortBy('description');
                            setSortOrder(params.sortDirection === 'ASC' ? 'asc' : 'desc');
                        } else if (params.sortBy === null) {
                            setSortBy('');
                            setSortOrder('desc');
                        }
                        onClose();
                    }}
                    currentSort={{
                        sortBy: sortBy,
                        sortDirection: sortOrder === 'asc' ? 'ASC' : 'DESC'
                    }}
                    currentDescriptionKeyword={columnFilters.descriptionKeyword}
                />
            ),
            isFilterActive: (filterState) => !!columnFilters.descriptionKeyword
        },
        {
            id: 'categoryId',
            label: 'Category',
            size: 180,
            onSort: () => handleColumnSort('category'),
            // The fixed-width searchable TreeDropdown below only makes sense
            // at desktop cell width - on a mobile card it just overflowed
            // the card entirely, so show the resolved category name as
            // plain text instead. Editing the category from mobile still
            // works via the Edit action, which opens the full modal.
            mobileElement: (value: any) => (
                <Typography sx={{ fontSize: 13 }}>
                    {categories.find(c => String(c.value) === String(value))?.label || '-'}
                </Typography>
            ),
            // Same searchable, tree-structured category picker as the
            // Transactions page's inline category cell, instead of the
            // generic flat-list dropdown (table-virtual.tsx's
            // type:'dropdown' handling) this used before.
            customElement: (value, row) => {
                const disabled = (row as any).isGroupRow || isGstProvision(row);
                return (
                    <div onClick={(e) => e.stopPropagation()}>
                        <TreeDropdown
                            enableHighlightMode
                            options={hierarchicalCategoryOptions}
                            value={value != null ? String(value) : null}
                            onChange={(selectedId) => {
                                const newCategoryId = selectedId ? Number(selectedId) : null;
                                handleUpdateProvision({ ...row, categoryId: newCategoryId } as Provision);
                            }}
                            placeholder="Select..."
                            height={24}
                            width={180}
                            borderColor="var(--color-primary)"
                            disabled={disabled}
                        />
                    </div>
                );
            },
            filterable: true,
            filterComponent: (open, anchorEl, onClose, onApply) => {
                const categoryOptions = categories.map(c => ({ id: String(c.value), label: c.label }));
                return (
                    <StatusFilter
                        open={open}
                        anchorEl={anchorEl}
                        onClose={onClose}
                        title="Filter by Category"
                        options={categoryOptions}
                        selectedIds={columnFilters.categoryIds?.length ? columnFilters.categoryIds : categoryOptions.map(o => o.id)}
                        searchable
                        searchPlaceholder="search category"
                        sortByValue="category"
                        sortFieldLabel="Category"
                        currentSort={{ sortBy, sortDirection: sortOrder === 'asc' ? 'ASC' : 'DESC' }}
                        onApply={(result) => {
                            setColumnFilters(prev => ({
                                ...prev,
                                categoryIds: result.selectedIds.length < categoryOptions.length ? result.selectedIds : undefined,
                            }));
                            if (result.sortBy) {
                                setSortBy(result.sortBy);
                                setSortOrder(result.sortDirection === 'ASC' ? 'asc' : 'desc');
                            }
                            onClose();
                        }}
                    />
                );
            },
            isFilterActive: () => !!columnFilters.categoryIds?.length,
        },
        {
            id: 'actions',
            label: 'Actions',
            size: 90,
            type: 'actions',
            showCheckbox: true,
            // GST-generated provisions are system-managed (fiscal-year-scoped,
            // recalculated by gst-provision-service.js) - merging them into a
            // manually-managed template would fight that recalculation, so
            // they're excluded from selection entirely, same as canEdit/canDelete.
            canSelect: (row) => !row.isGroupRow && !isGstProvision(row),
            canEdit: (row) => !row.isGroupRow && !isGstProvision(row),
            canDelete: (row) => !row.isGroupRow && !isGstProvision(row),
            onDelete: (row) =>{ setOpenModal('delete'); setSelectedProvision(row as Provision) },
            onEdit: (row) =>{
                if (row.staffPaymentId) {
                    return navigate(`/staff?editStaffId=${row.staffPaymentId}`);
                }
                setOpenModal('edit');
                setSelectedProvision(row as Provision)
            }
        },
    ];

    // General Provisions - the company's General Income / General Expense pair
    // (general-provision-controller.js), shown above the recurring list in
    // the same table layout. Each one's amount is its unlinked transactions
    // averaged per period, which the Recurrence badge shows. The columns
    // keep their look but drop sorting/filtering and selection; everything
    // is edited from the Edit modal.
    const generalProvisionRows = (fetchGeneral.data?.data ?? []).map(p => ({
        ...p,
        id: `general-${p.kind}`,
        isGeneralRow: true,
        type: p.period,
    }));
    // Recurrence, Amount, Description, Considered Date Range, Category,
    // Status, Actions - reusing the recurring table's own cells where they
    // exist, minus sorting/filtering.
    const generalColumnOrder = ['type', 'amount', 'description', 'consideredRange', 'categoryId', 'status', 'actions'];
    const generalExtraColumns: Column[] = [
        {
            id: 'consideredRange',
            label: 'Considered Date Range',
            size: 200,
            // "Last 90 days" - the exact dates are on hover.
            customElement: (_value, row) => (
                <Tooltip
                    title={row.consideredFrom && row.consideredTo
                        ? `${formatDate?.(new Date(`${row.consideredFrom}T00:00:00`)) ?? row.consideredFrom} - ${formatDate?.(new Date(`${row.consideredTo}T00:00:00`)) ?? row.consideredTo}`
                        : ''}
                    arrow
                >
                    <Typography sx={{ fontSize: 14 }}>
                        {row.lookbackDays ? `Last ${row.lookbackDays} day${row.lookbackDays === 1 ? '' : 's'}` : '-'}
                    </Typography>
                </Tooltip>
            ),
        },
        {
            id: 'status',
            label: 'Status',
            size: 100,
            // On mobile it sits under the description on the card itself
            // (see the description override below) instead of behind "Show more".
            hideOnMobile: true,
            customElement: (_value, row) => (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Chip
                        size="small"
                        label={row.enabled ? 'Enabled' : 'Disabled'}
                        sx={{
                            fontWeight: 700,
                            fontSize: 12,
                            color: row.enabled ? '#fff' : 'var(--color-text-muted)',
                            background: row.enabled ? 'var(--color-primary)' : 'var(--color-container-background)',
                        }}
                    />
                </Box>
            ),
        },
    ];
    const generalColumns: Column[] = generalColumnOrder.map(id => {
        const extra = generalExtraColumns.find(c => c.id === id);
        if (extra) return extra;
        const col = columns.find(c => c.id === id)!;
        const base: Column = { ...col, onSort: undefined, filterable: false, filterComponent: undefined, isFilterActive: undefined };
        if (col.id === 'type') return { ...base, customElement: (value, row) => (value ? col.customElement?.(value, row) : null) };
        // Mobile card: the name with its Enabled/Disabled status underneath,
        // so the status shows without expanding the card.
        if (col.id === 'description') {
            return {
                ...base,
                mobileElement: (value, row) => (
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography sx={{ ...MOBILE_NAME_SX, color: getRowFontSx(row).color }}>{value}</Typography>
                        <Typography sx={{ fontSize: 10, fontWeight: 700, color: row.enabled ? 'var(--color-primary)' : 'var(--color-text-muted)' }}>
                            {row.enabled ? 'ENABLED' : 'DISABLED'}
                        </Typography>
                    </Box>
                ),
            };
        }
        if (col.id === 'categoryId') {
            return {
                ...base,
                customElement: (value, row) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <TreeDropdown
                            options={hierarchicalCategoryOptions}
                            value={value != null ? String(value) : null}
                            onChange={() => {}}
                            placeholder="Select..."
                            height={24}
                            width={180}
                            borderColor="var(--color-primary)"
                            disabled
                        />
                        {/* Picked by AI from the company's own categories,
                            until someone chooses one in the edit modal. */}
                        {row.categorySource === 'ai' && value != null && (
                            <Chip size="small" label="AI" title="Picked by AI from your categories" sx={{ height: 18, fontSize: 10, fontWeight: 700, color: 'var(--color-primary)', background: 'rgba(var(--color-primary-rgb), 0.1255)' }} />
                        )}
                    </Box>
                ),
                // Mobile card: the category name (no dropdown room), with the
                // same AI tag while the AI is still the one choosing it.
                mobileElement: (value, row) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Typography sx={{ fontSize: 13 }}>
                            {categories.find(c => String(c.value) === String(value))?.label || '-'}
                        </Typography>
                        {row.categorySource === 'ai' && value != null && (
                            <Chip size="small" label="AI" sx={{ height: 16, fontSize: 9, fontWeight: 700, color: 'var(--color-primary)', background: 'rgba(var(--color-primary-rgb), 0.1255)' }} />
                        )}
                    </Box>
                ),
            };
        }
        // Same "..." menu as a recurring row, Edit only - no selection or delete
        // for this fixed pair.
        if (col.id === 'actions') {
            return {
                ...base,
                showCheckbox: false,
                canSelect: () => false,
                canEdit: () => true,
                canDelete: () => false,
                onDelete: undefined,
                onEdit: (row) => setEditingGeneral((fetchGeneral.data?.data ?? []).find(p => p.kind === row.kind) || null),
            };
        }
        return base;
    });

    // Row click opens "Related Transactions" (formerly its own action-menu
    // entry) - GST and grouped rows have their own dedicated "View" actions
    // covering this same ground (generated transactions for GST, member
    // templates for a group), and staff-payment rows have nothing to show
    // here, so clicking those does nothing rather than guessing which view
    // they'd want. Interactive cells (checkbox, actions menu, inline
    // category edit) stop propagation before this ever fires - see
    // table-virtual.tsx.
    const handleProvisionRowClick = (row: Record<string, any>) => {
        if (row.isGroupRow || isGstProvision(row) || row.staffPaymentId) return;
        setSelectedProvision(row as Provision);
        setOpenModal('view-related');
    };

    const additionalActions = [
        {
            label: 'Terminate',
            icon: 'warning' as const,
            visible: (row: Record<string, any>) => {
                if (row.isGroupRow) return false;
                if (row.staffPaymentId) return false;
                if (isGstProvision(row)) return false;
                const hasEnded = row.endDate && dayjs(row.endDate).isBefore(dayjs().startOf('day'));
                return !hasEnded;
            },
            disabled: !canUpdate,
            onClick: (row: Record<string, any>) => {
                setTerminateEndDate(dayjs().format('YYYY-MM-DD'));
                setOpenModal('terminate');
                setSelectedProvision(row as Provision);
            },
        },
        {
            label: 'View',
            icon: 'eye' as const,
            visible: (row: Record<string, any>) => !row.isGroupRow && isGstProvision(row),
            onClick: (row: Record<string, any>) => {
                setSelectedProvision(row as Provision);
                setOpenModal('view-gst');
            },
        },
        {
            label: 'View',
            icon: 'eye' as const,
            visible: (row: Record<string, any>) => !!row.isGroupRow,
            onClick: (row: Record<string, any>) => setViewingGroupId(row.groupId),
        },
        {
            label: 'Ungroup',
            icon: 'close' as const,
            visible: (row: Record<string, any>) => !!row.isGroupRow,
            disabled: !canUpdate,
            onClick: (row: Record<string, any>) => setConfirmUngroupId(row.groupId),
        },
    ];

    const periodsPerYear = TOTALS_PERIODS[totalsPeriod].perYear;
    const provisionTotalsEl = provisionTotals && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {[
                { label: 'Income', value: provisionTotals.yearlyIncome / periodsPerYear, color: 'var(--color-positive-amount, #1a7f37)' },
                { label: 'Expense', value: provisionTotals.yearlyExpense / periodsPerYear, color: 'var(--color-negative-amount)' },
            ].map(t => (
                <Box key={t.label} component="span" sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.75, px: 1, py: 0.5, borderRadius: 1, background: 'var(--color-container-background)', whiteSpace: 'nowrap' }}>
                    <Box component="span" sx={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-muted)' }}>{t.label}</Box>
                    <Box component="span" sx={{ fontSize: 15, fontWeight: 700, color: t.color, fontVariantNumeric: 'tabular-nums' }}>
                        {formatAmount ? formatAmount(t.value) : t.value.toFixed(2)}
                    </Box>
                </Box>
            ))}
            <TextField
                select
                size="small"
                value={totalsPeriod}
                onChange={e => setTotalsPeriod(e.target.value as keyof typeof TOTALS_PERIODS)}
                SelectProps={{ native: true }}
                inputProps={{ 'aria-label': 'Totals period' }}
                sx={{ '& .MuiInputBase-root': { height: 30, fontSize: 13 }, '& select': { py: 0.5 } }}
            >
                {Object.entries(TOTALS_PERIODS).map(([key, period]) => (
                    <option key={key} value={key}>{period.label}</option>
                ))}
            </TextField>
        </Box>
    );

    return (
        <div style={{ width: '100%' }}>
            <AddProvisionPopup
                open={openModal === 'add' || openModal === 'edit' || openModal === 'merge' || (openModal === 'provision' && editingProvisionId !== null)}
                isEdit={{ id: editingProvisionId || selectedProvision?.id || null, state: openModal === 'edit' || (openModal === 'provision' && editingProvisionId !== null) }}
                onClose={() => {
                    setOpenModal('');
                    setEditingProvisionId(null);
                    setMergeSourceIds(null);
                }}
                onCreate={(newProvision) => {
                    if (openModal === 'merge' && mergeSourceIds) {
                        handleMergeCreate(newProvision, mergeSourceIds);
                    } else if (newProvision.id) {
                        handleUpdateProvision(newProvision);
                    } else {
                        handleCreateProvision(newProvision);
                    }
                }}
                onDelete={handleDeleteProvision}
                isLoading={create.isLoading || update.isLoading || mergeApi.isLoading}
                mergeSources={openModal === 'merge' ? mergeSourcesForDisplay : undefined}
                mergeYearlyTotal={openModal === 'merge' ? mergeYearlyTotal : undefined}
                mergeDefaults={openModal === 'merge' ? mergeDefaults || undefined : undefined}
                mergeCategories={categories}
            />
            <DeleteModal
                open={openModal === 'delete'}
                message="Delete Provision?"
                description="This action will permanently remove the selected provision."
                image={{ src: provisionImage, width: 150, height: 150 }}
                onCancel={() => {setOpenModal(''); setSelectedProvision(null);}}
                onConfirm={() => {
                    if (selectedProvision) {
                        const { url, method } = buildApiRequestConfig(routes.provisionTemplate.delete, [{ param: 'id', value: selectedProvision.id }]);
                        setRemove({ url, method });
                    }
                }}
            />
            <DateConfirmModal
                open={openModal === 'terminate'}
                message="Terminate Provision?"
                description="This will end the provision on the date you choose. Occurrences after that date will be removed; anything already linked to a real transaction is left untouched."
                confirmLabel="Terminate"
                dateLabel="End Date"
                value={terminateEndDate}
                minDate={selectedProvision?.startDate}
                onChange={setTerminateEndDate}
                confirmDisabled={!terminateEndDate}
                onCancel={() => { setOpenModal(''); setSelectedProvision(null); setTerminateEndDate(''); }}
                onConfirm={() => {
                    if (selectedProvision?.id && terminateEndDate) {
                        handleTerminateProvision(selectedProvision.id, terminateEndDate);
                    }
                }}
            />
            <GstProvisionDetail
                open={openModal === 'view-gst'}
                provisionTemplateId={selectedProvision?.id ?? null}
                onClose={() => { setOpenModal(''); setSelectedProvision(null); }}
            />
            <RelatedTransactionsDetail
                open={openModal === 'view-related'}
                provisionTemplateId={selectedProvision?.id ?? null}
                description={selectedProvision?.description}
                amount={selectedProvision?.amount}
                startDate={selectedProvision?.startDate}
                endDate={selectedProvision?.endDate}
                endDateType={selectedProvision?.endDateType}
                occurrenceCount={selectedProvision?.occurrenceCount}
                autoProvisionMeta={selectedProvision?.autoProvisionMeta}
                onClose={() => { setOpenModal(''); setSelectedProvision(null); }}
            />
            <ProvisionGroupDetail
                open={viewingGroupId !== null}
                groupName={viewingGroupName}
                members={viewingGroupMembers}
                categories={categories}
                canUpdate={canUpdate}
                onClose={() => setViewingGroupId(null)}
                onUngroup={() => { if (viewingGroupId !== null) setConfirmUngroupId(viewingGroupId); }}
                onRemoveMembers={handleRemoveGroupMembers}
            />
            <DeleteModal
                open={confirmUngroupId !== null}
                message="Ungroup All Provisions?"
                description="This dissolves the group and shows its provisions as individual rows again. This doesn't delete them."
                confirmLabel="Ungroup"
                onCancel={() => setConfirmUngroupId(null)}
                onConfirm={() => { if (confirmUngroupId !== null) handleUngroup(confirmUngroupId); }}
            />
            <Dialog
                open={groupDialogOpen}
                onClose={() => { setGroupDialogOpen(false); setNewGroupName(''); setTargetGroupId(''); setGroupMode('new'); }}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Group {selectedGroupCandidateIds.length} Provisions</DialogTitle>
                <DialogContent>
                    {existingGroups.length > 0 && (
                        <RadioGroup
                            value={groupMode}
                            onChange={(e) => setGroupMode(e.target.value as 'new' | 'existing')}
                            sx={{ mb: 1 }}
                        >
                            <FormControlLabel value="new" control={<Radio size="small" />} label="Create a new group" />
                            <FormControlLabel
                                value="existing"
                                control={<Radio size="small" />}
                                label="Add to an existing group"
                                disabled={selectedGroupCandidateIds.length === 0}
                            />
                        </RadioGroup>
                    )}
                    {groupMode === 'existing' ? (
                        <Dropdown
                            options={existingGroups}
                            value={targetGroupId}
                            onChange={setTargetGroupId}
                            placeholder="Select a group"
                            title="Group"
                        />
                    ) : (
                        <TextField
                            autoFocus
                            fullWidth
                            label="Group Name"
                            value={newGroupName}
                            onChange={(e) => setNewGroupName(e.target.value)}
                            sx={{ mt: 1 }}
                        />
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => { setGroupDialogOpen(false); setNewGroupName(''); setTargetGroupId(''); setGroupMode('new'); }} sx={{ textTransform: 'none', color: 'var(--color-text-muted)' }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disabled={
                            group.isLoading ||
                            (groupMode === 'existing'
                                ? !targetGroupId || selectedGroupCandidateIds.length === 0
                                : !newGroupName.trim() || selectedGroupCandidateIds.length < 2)
                        }
                        onClick={() => handleGroupSelected(selectedGroupCandidateIds)}
                        sx={{ textTransform: 'none', fontWeight: 600, background: 'var(--color-primary)', color: '#fff', boxShadow: 'none', '&:hover': { background: 'var(--color-primary)' } }}
                    >
                        {groupMode === 'existing' ? 'Add to Group' : 'Create Group'}
                    </Button>
                </DialogActions>
            </Dialog>
            <Topbar
                title="Provisions"
                isMobile={isMobile}
                mobileMenuButton={
                    isMobile && (
                        <Button 
                            variant="contained" 
                            color="primary" 
                            onClick={() => setSidebarOpen(true)} 
                            sx={{ 
                                color: '#fff', 
                                height: 40,
                                display: 'flex',
                                gap: 1,
                                minWidth: 40,
                                p: 1,
                                backgroundColor: 'var(--color-primary)', 
                                '&:hover': { backgroundColor: 'var(--color-primary)' } 
                            }}
                        >
                            <Menu size={20} color="#fff"/>
                        </Button>
                    )
                }
            >
                {!isMobile && (
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', width: '100%', justifyContent: 'flex-end' }}>
                        {selectedGroupCandidateIds.length >= 2 && (
                            <Button
                                variant="outlined"
                                startIcon={<Users size={16} />}
                                disabled={!canUpdate}
                                onClick={() => handleMergeSelected(selectedGroupCandidateIds)}
                                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, borderColor: 'var(--color-primary)', color: 'var(--color-primary)' }}
                            >
                                Merge Selected ({selectedGroupCandidateIds.length})
                            </Button>
                        )}
                        {legacyGroupingEnabled && (selectedGroupCandidateIds.length >= 2 || (selectedGroupCandidateIds.length >= 1 && existingGroups.length > 0)) && (
                            <Button
                                variant="outlined"
                                startIcon={<Users size={16} />}
                                disabled={!canUpdate}
                                onClick={() => {
                                    setGroupMode(selectedGroupCandidateIds.length < 2 ? 'existing' : 'new');
                                    setGroupDialogOpen(true);
                                }}
                                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, borderColor: 'var(--color-text-muted)', color: 'var(--color-text-muted)' }}
                            >
                                Legacy Group ({selectedGroupCandidateIds.length})
                            </Button>
                        )}
                        <AddBtn disabled={!canCreate} onClick={() => { setOpenModal('add'); setSelectedProvision(null); }} label="Add Provision" />
                    </Box>
                )}
            </Topbar>
            {isMobile && canUpdate && (selectedGroupCandidateIds.length >= 2 || (legacyGroupingEnabled && selectedGroupCandidateIds.length >= 1 && existingGroups.length > 0)) && (
                <Box sx={{
                    position: 'fixed',
                    bottom: 20,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 999,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1,
                }}>
                    {selectedGroupCandidateIds.length >= 2 && (
                        <Button
                            variant="contained"
                            startIcon={<Users size={16} />}
                            onClick={() => handleMergeSelected(selectedGroupCandidateIds)}
                            sx={{
                                borderRadius: 999,
                                textTransform: 'none',
                                fontWeight: 600,
                                whiteSpace: 'nowrap',
                                px: 2.5,
                                py: 1,
                                background: 'var(--color-primary)',
                                color: '#fff',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
                                '&:hover': { background: 'var(--color-primary)' },
                            }}
                        >
                            Merge Selected ({selectedGroupCandidateIds.length})
                        </Button>
                    )}
                    {legacyGroupingEnabled && (selectedGroupCandidateIds.length >= 2 || (selectedGroupCandidateIds.length >= 1 && existingGroups.length > 0)) && (
                        <Button
                            variant="contained"
                            startIcon={<Users size={16} />}
                            onClick={() => {
                                setGroupMode(selectedGroupCandidateIds.length < 2 ? 'existing' : 'new');
                                setGroupDialogOpen(true);
                            }}
                            sx={{
                                borderRadius: 999,
                                textTransform: 'none',
                                fontWeight: 600,
                                whiteSpace: 'nowrap',
                                px: 2.5,
                                py: 1,
                                background: '#fff',
                                color: 'var(--color-text-muted)',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
                                '&:hover': { background: '#fff' },
                            }}
                        >
                            Legacy Group ({selectedGroupCandidateIds.length})
                        </Button>
                    )}
                    <Button
                        variant="contained"
                        onClick={() => setSelectedIndices([])}
                        sx={{
                            borderRadius: 999,
                            textTransform: 'none',
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                            px: 2.5,
                            py: 1,
                            background: '#fff',
                            color: 'var(--color-text-muted)',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
                            '&:hover': { background: '#fff' },
                        }}
                    >
                        Clear
                    </Button>
                </Box>
            )}
            <GeneralProvisionModal
                open={!!editingGeneral}
                provision={editingGeneral}
                categoryOptions={hierarchicalCategoryOptions}
                onClose={() => setEditingGeneral(null)}
                onSaved={refetchGeneral}
            />
            <div style={{ padding: '10px 10px 0' }}>
                <DynamicTableVirtual
                    sectionLabel="General Provisions"
                    additionalActions={[{
                        label: (row) => (row.enabled ? 'Disable' : 'Enable'),
                        icon: (row) => (row.enabled ? 'close' : 'check'),
                        onClick: toggleGeneralStatus,
                        disabled: !canUpdate || generalStatus.isLoading,
                    }]}
                    isLoading={fetchGeneral.isLoading && !fetchGeneral.data}
                    columns={generalColumns}
                    data={generalProvisionRows}
                    fontSx={getRowFontSx}
                    canEditRows={canUpdate}
                    hideMobileFilterBar
                    mobileCardStyle={(row, idx) => ({
                        background: idx % 2 === 0 ? 'var(--color-provision-row-bg)' : 'rgba(var(--color-provision-row-bg-rgb), 0.5)',
                        border: '#ffe5f0',
                    })}
                />
            </div>
            <div style={{ padding: '10px' }}>
                <DynamicTableVirtual
                    sectionLabel="Recurring Provisions"
                    // The search filters this list (not General Provisions), so it
                    // sits on this section rather than the page header.
                    header={isMobile ? (
                        <Box sx={{ px: 1, pb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <HeaderSearch
                                value={columnFilters.descriptionKeyword}
                                onSearch={(v) => setColumnFilters(prev => ({ ...prev, descriptionKeyword: v || undefined }))}
                                placeholder="Search provisions..."
                                width="100%"
                            />
                            {totalProvisionsCount != null && (
                                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', px: 1, py: 0.5, borderRadius: 1, fontSize: 16, fontWeight: 600, background: 'var(--color-page-background)', color: 'var(--color-text-muted)', flexShrink: 0 }}>
                                    {totalProvisionsCount}
                                </Box>
                            )}
                            <Box
                                onClick={() => setCombinedFilterOpen(true)}
                                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: 1, cursor: 'pointer', flexShrink: 0 }}
                            >
                                <SlidersHorizontal size={20} color="var(--color-primary)" />
                            </Box>
                        </Box>
                        {provisionTotalsEl && <Box sx={{ mt: 1 }}>{provisionTotalsEl}</Box>}
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                            <HeaderSearch
                                value={columnFilters.descriptionKeyword}
                                onSearch={(v) => setColumnFilters(prev => ({ ...prev, descriptionKeyword: v || undefined }))}
                                placeholder="Search provisions..."
                            />
                            {totalProvisionsCount != null && (
                                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', px: 1, py: 0.5, borderRadius: 1, fontSize: 16, fontWeight: 600, background: 'var(--color-container-background)', color: 'var(--color-text-muted)' }}>
                                    {totalProvisionsCount}
                                </Box>
                            )}
                            {provisionTotalsEl}
                        </Box>
                    )}
                    columns={columns}
                    data={displayData}
                    isLoading={fetch.isLoading && !isLoadingMoreProvisions}
                    columnFilters={columnFilters}
                    scrollMode="page"
                    onColumnFilterChange={setColumnFilters}
                    sortState={sortBy ? { column: getSortColumnId(sortBy), direction: sortOrder === 'asc' ? 'ASC' : 'DESC' } : undefined}
                    hideMobileFilterBar
                    fontSx={getRowFontSx}
                    canEditRows={canUpdate}
                    canDeleteRows={canDelete}
                    additionalActions={additionalActions}
                    onRowClick={handleProvisionRowClick}
                    hasNextPage={hasMoreProvisions}
                    onLoadMore={handleLoadMoreProvisions}
                    isLoadingMore={isLoadingMoreProvisions}
                    selectedIndices={selectedIndices}
                    onSelectionChange={setSelectedIndices}
                    // Same pink provision-row tint (alternated by row index)
                    // the Transactions page's mobile card uses for its own
                    // provision rows - every row here IS a provision, so it
                    // should carry that same color identity instead of the
                    // generic flat gray card.
                    mobileCardStyle={(row, idx) => ({
                        background: idx % 2 === 0 ? 'var(--color-provision-row-bg)' : 'rgba(var(--color-provision-row-bg-rgb), 0.5)',
                        border: '#ffe5f0',
                    })}
                />
            </div>
            <SidebarMobileDrawer
                open={sidebarOpen}
                onClose={() => setSidebarOpen(false)}
            />
            {isMobile && (
                <BottomSheetDialog open={combinedFilterOpen} onClose={() => setCombinedFilterOpen(false)} heightVh={88}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, pb: 1, flexShrink: 0, borderBottom: '1px solid #eee' }}>
                        <Typography sx={{ fontWeight: 700, fontSize: 18 }}>Sort & Filter</Typography>
                        <IconButton size="small" onClick={() => setCombinedFilterOpen(false)}>
                            <X size={20} />
                        </IconButton>
                    </Box>
                    <Box sx={{ px: 2, py: 2, overflowY: 'auto', flex: 1 }}>
                        <CombinedFilterSection title="Start Date" section="startDate">
                            <SimpleDateFilter
                                ref={startDateFilterRef}
                                open={false}
                                anchorEl={null}
                                onClose={() => {}}
                                embedded
                                hideActions
                                onApply={handleCombinedFilterDraftApply}
                                currentSort={{ sortBy, sortDirection: sortOrder === 'asc' ? 'ASC' : 'DESC' }}
                                currentStartDate={columnFilters.startDateFrom}
                                currentEndDate={columnFilters.startDateTo}
                                fieldLabel="Start Date"
                                sortByValue="startDate"
                            />
                        </CombinedFilterSection>
                        <CombinedFilterSection title="End Date" section="endDate">
                            <SimpleDateFilter
                                ref={endDateFilterRef}
                                open={false}
                                anchorEl={null}
                                onClose={() => {}}
                                embedded
                                hideActions
                                onApply={handleCombinedFilterDraftApply}
                                currentSort={{ sortBy, sortDirection: sortOrder === 'asc' ? 'ASC' : 'DESC' }}
                                currentStartDate={columnFilters.endDateFrom}
                                currentEndDate={columnFilters.endDateTo}
                                fieldLabel="End Date"
                                sortByValue="endDate"
                                extraToggle={{ label: 'Hide past end dates', currentValue: columnFilters.hideExpiredRecurring !== false }}
                            />
                        </CombinedFilterSection>
                        <CombinedFilterSection title="Recurrence" section="recurrence">
                            <StatusFilter
                                ref={recurrenceFilterRef}
                                open={false}
                                anchorEl={null}
                                onClose={() => {}}
                                embedded
                                hideActions
                                title="Filter by Recurrence"
                                options={recurrenceOptions}
                                selectedIds={columnFilters.recurrencePatterns?.length ? columnFilters.recurrencePatterns : recurrenceOptions.map(o => o.id)}
                                sortByValue="recurrencePattern"
                                sortFieldLabel="Recurrence"
                                currentSort={{ sortBy, sortDirection: sortOrder === 'asc' ? 'ASC' : 'DESC' }}
                                onApply={(result) => handleCombinedFilterDraftApply({
                                    recurrencePatterns: result.selectedIds.length < recurrenceOptions.length ? result.selectedIds : undefined,
                                    ...(result.sortBy ? { sortBy: result.sortBy, sortDirection: result.sortDirection } : {}),
                                })}
                            />
                        </CombinedFilterSection>
                        <CombinedFilterSection title="Amount" section="amount">
                            <AmountFilter
                                ref={amountCombinedFilterRef}
                                open={false}
                                anchorEl={null}
                                onClose={() => {}}
                                embedded
                                hideActions
                                min={amountBounds.min}
                                max={amountBounds.max}
                                onApply={handleCombinedFilterDraftApply}
                                currentSort={{ sortBy, sortDirection: sortOrder === 'asc' ? 'ASC' : 'DESC' }}
                                currentAmountKeyword={columnFilters.amountKeyword}
                            />
                        </CombinedFilterSection>
                        <CombinedFilterSection title="Category" section="category">
                            <StatusFilter
                                ref={categoryCombinedFilterRef}
                                open={false}
                                anchorEl={null}
                                onClose={() => {}}
                                embedded
                                hideActions
                                title="Filter by Category"
                                options={categories.map(c => ({ id: String(c.value), label: c.label }))}
                                selectedIds={columnFilters.categoryIds?.length ? columnFilters.categoryIds : categories.map(c => String(c.value))}
                                searchable
                                searchPlaceholder="search category"
                                sortByValue="category"
                                sortFieldLabel="Category"
                                currentSort={{ sortBy, sortDirection: sortOrder === 'asc' ? 'ASC' : 'DESC' }}
                                onApply={(result) => handleCombinedFilterDraftApply({
                                    categoryIds: result.selectedIds.length < categories.length ? result.selectedIds : undefined,
                                    ...(result.sortBy ? { sortBy: result.sortBy, sortDirection: result.sortDirection } : {}),
                                })}
                            />
                        </CombinedFilterSection>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, p: 2, pt: 1.5, flexShrink: 0, borderTop: '1px solid #eee' }}>
                        <Button
                            fullWidth
                            variant="contained"
                            sx={{ background: 'var(--color-primary)', color: '#fff', borderRadius: 2, fontWeight: 600, boxShadow: 'none', '&:hover': { background: 'var(--color-primary)' } }}
                            onClick={handleApplyAllCombinedFilters}
                        >
                            Apply Now
                        </Button>
                        <Button
                            fullWidth
                            variant="contained"
                            sx={{ background: 'var(--color-container-background)', color: 'var(--color-text-muted)', borderRadius: 2, fontWeight: 600, boxShadow: 'none', '&:hover': { background: 'var(--color-container-background)' } }}
                            onClick={handleClearAllCombinedFilters}
                        >
                            Clear
                        </Button>
                    </Box>
                </BottomSheetDialog>
            )}
            {isMobile && canCreate && (
                <Fab
                    onClick={() => { setOpenModal('add'); setSelectedProvision(null); }}
                    sx={{
                        position: 'fixed',
                        bottom: 20,
                        right: 20,
                        background: 'var(--color-primary)',
                        color: '#fff',
                        '&:hover': { background: 'var(--color-primary-hover)' },
                        zIndex: 999,
                    }}
                >
                    <Plus size={24} />
                </Fab>
            )}
        </div>
    );
};

export default Provisions;